import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.id === Number(id)) || fallbackArticle;

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 pt-20 pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-5xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 font-heading mb-3">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-8">您访问的文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    ...(category ? [{ label: category.name, path: '/category/all' }] : []),
    { label: article.title },
  ];

  const formatContent = (text: string) => {
    return text.split('\n\n').map((p) => p.trim()).filter(Boolean);
  };

  const paragraphs = formatContent(article.content);

  return (
    <>
      <title>{`${article.title} - 91黑料吃瓜网`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`91黑料吃瓜网, ${article.tags.join(', ')}, 吃瓜事件, 黑料曝光`} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`http://aywxtna.cn/article/${article.id}`} />
      <meta property="og:title" content={`${article.title} - 91黑料吃瓜网`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`http://aywxtna.cn/article/${article.id}`} />
      <meta property="og:image" content={article.image} />
      <meta property="og:locale" content="zh_CN" />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - 91黑料吃瓜网`} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.image} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.image,
        "datePublished": article.date,
        "author": {
          "@type": "Person",
          "name": article.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "91黑料吃瓜网",
          "url": "http://aywxtna.cn/"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `http://aywxtna.cn/article/${article.id}`
        }
      }) }} />

      <Breadcrumb items={breadcrumbItems} />

      <article className="w-full px-4 md:px-6 pt-6 pb-10 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {article.exclusive && (
              <span className="px-2.5 py-1 text-xs font-semibold bg-accent-500 text-white rounded whitespace-nowrap">
                独家爆料
              </span>
            )}
            {article.hot && (
              <span className="px-2.5 py-1 text-xs font-semibold bg-primary-100 text-primary-700 rounded whitespace-nowrap">
                热门
              </span>
            )}
            {category && (
              <Link
                to="/category/all"
                className="px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {category.name}
              </Link>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading leading-snug mb-5">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8 pb-6 border-b border-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万阅读` : `${article.views}阅读`}
            </span>
          </div>

          {article.image && (
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          )}

          <div className="prose prose-sm md:prose-base max-w-none">
            {paragraphs.map((p, idx) => (
              <p key={idx} className="text-foreground-800 leading-relaxed mb-5 text-[15px] md:text-base">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to="/category/all"
                  className="group block bg-background-50 rounded-lg overflow-hidden transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{ra.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {ra.views >= 10000 ? `${(ra.views / 10000).toFixed(0)}万` : ra.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}